import { SetupTime, SetupTimePayload } from 'Interfaces/ReferenceInterfaces/SetupTimeInterface';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { setupTimeServices } from 'services/ReferenceScreenServices/setupTimeServices';

export class SetupTimeStore {
  setupTimes: SetupTime[] = [];
  isLoading: boolean = false;
  isPolling: boolean = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  selectedSetupTime: SetupTime | null = null;
  environment = import.meta.env.VITE_ENVIRONMENT;

  constructor() {
    makeAutoObservable(this);
  }

  // Initial load method (with loading indicator)
  fetchSetupTimes = async (page = 1, pageSize = 10000) => {
    this.isLoading = true;
    try {
      const response = await setupTimeServices.getAllSetupTimes(page, pageSize);
      runInAction(() => {
        this.setupTimes = response.data;
        this.pagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
      });
    } catch (error) {
      toast.error('Error fetching setup times');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Polling method (without loading indicator)
  pollSetupTimes = async (page = 1, pageSize = 10000) => {
    this.isPolling = true;
    try {
      const response = await setupTimeServices.getAllSetupTimes(page, pageSize);
      runInAction(() => {
        // Update data only if it has changed
        if (JSON.stringify(this.setupTimes) !== JSON.stringify(response.data)) {
          this.setupTimes = response.data;
          this.pagination = {
            currentPage: response.pagination.current_page,
            totalPages: response.pagination.total_pages,
            totalItems: response.pagination.total_items,
            hasNext: response.pagination.has_next,
            hasPrevious: response.pagination.has_previous
          };
        }
      });
    } catch (error) {
      // Don't show toast for polling errors to avoid spam
      if (this.environment === 'development') {
        console.error('Error polling setup times:', error);
      }
      toast.error('Error polling setup times!');
    } finally {
      runInAction(() => {
        this.isPolling = false;
      });
    }
  };

  // Method to refresh data (can be called manually)
  refreshData = async (page = 1, pageSize = 10000) => {
    await this.fetchSetupTimes(page, pageSize);
  };

  createSetupTime = async (setupTimeData: SetupTimePayload): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await setupTimeServices.createSetupTime(setupTimeData);
      if (response.status) {
        toast.success('Setup time created successfully');
        await this.fetchSetupTimes(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to create setup time');
        return false;
      }
    } catch (error) {
      toast.error('Error creating setup time');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateSetupTime = async (id: number, setupTimeData: SetupTimePayload): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await setupTimeServices.updateSetupTime(id, setupTimeData);
      if (response.status) {
        toast.success('Setup time updated successfully');
        await this.fetchSetupTimes(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to update setup time');
        return false;
      }
    } catch (error) {
      toast.error('Error updating setup time');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteSetupTime = async (id: number) => {
    this.isLoading = true;
    try {
      const response = await setupTimeServices.deleteSetupTime(id);
      if (response.status) {
        toast.success('Setup time deleted successfully');

        const isLastItemOnPage = this.setupTimes.length === 1;
        const isLastPage = this.pagination.currentPage === this.pagination.totalPages;

        if (isLastItemOnPage && !isLastPage && this.pagination.currentPage > 1) {
          await this.fetchSetupTimes(this.pagination.currentPage - 1);
        } else {
          await this.fetchSetupTimes(this.pagination.currentPage);
        }
      } else {
        toast.error(response.message || 'Failed to delete setup time');
      }
    } catch (error) {
      toast.error('Error deleting setup time');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setSelectedSetupTime = (setupTime: SetupTime | null) => {
    this.selectedSetupTime = setupTime;
  };
}

export const setupTimeStore = new SetupTimeStore();
